import { getStore, RAW_STORE, Store, WRITE_STORE } from "./sockdrive-store";
import { compress, compressBound, uncompress } from "./mini-lz4";
import { LocalDriveImage } from "../emulators";

/** Ranges the boot preload fetches at once. The game is blocked on the
 *  preload (sockdrive_open waits for wc-sockdrive-preload-done), so nothing
 *  else shares the line yet. One at a time cost a full round trip — plus a
 *  cold disk read on the chunk host — per 256 KB range: Constructor's 61 MB
 *  took 13 s on an idle host and minutes on a busy one; 12 at once took
 *  2.3–3.7 s, 24 took 1.6–3.1 s (dos.games, 2026-10-20). Over HTTP/2 these
 *  are streams on one connection; an HTTP/1.1 host gets the browser's 6. */
const PRELOAD_CONCURRENCY = 24;

interface DriveInfo {
    ahead_read: number;
    range_count: number;
    dropped_ranges: number[];
    preload_ranges: number[] | "_";
    small_ranges: number[];
    cylinders: number;
    heads: number;
    sectors: number;
    sector_size: number;
    size: number;
    name: string;
    url: string;
    /** "cd" for flat streamed CD images (sockcd) — read-only byte streams
     *  with no FAT geometry. Absent on classic HDD drives. */
    kind?: string;
    /** Exact byte length of the image (`size` is KB and may round up). */
    size_bytes?: number;

    preloadSizeInBytes: number;
    sizeInBytes: number;
    readInBytes: number;
    writeInBytes: number;
}

export interface Drive {
    info: DriveInfo;
    range(sector: number): number;
    readRangeAsync(range: number): void;
    /** Starts the preload; resolves once the WHOLE queue has been delivered
     *  to the wasm RangeCache (protocol.ts then posts
     *  wc-sockdrive-preload-done so the blocked sockdrive_open returns). */
    ready(): Promise<void>;
    write(sector: number, buffer: Uint8Array): void;
    persist(): Promise<Uint8Array | null>;
    /** Previously persisted in-game writes as absolute sectors — replayed
     *  into the wasm's write cache at open (see wc-sockdrive-restore-sectors). */
    writtenSectors(): Array<{ sector: number; data: Uint8Array }>;
    /** Persist a range the wasm fetched synchronously itself, so the next
     *  session's preload finds it in the store (ws-sockdrive-range-loaded). */
    storeRange(range: number, data: Uint8Array): Promise<void>;
}

/** Reserved RAW_STORE key holding the cached `sockdrive.metaj` bytes —
 *  negative so it can never collide with a range number. The dos.games
 *  offline prefetcher writes the same key (lib/install/sockdrive-cache.ts);
 *  keep the two in sync. */
export const METAJ_STORE_KEY = -1;

/** Max locally-cached bytes a CD tree may drain into the wasm RangeCache
 *  while ONLINE. The cache never evicts, so this is a permanent heap cost —
 *  big enough for every audio track + modest data images (a fully installed
 *  game plays its music with zero network), small enough that a huge CD bin
 *  (600 MB rips) keeps streaming instead of bloating the heap. */
const CD_DRAIN_CAP_BYTES = 128 * 1024 * 1024;

/**
 * The metaj a local drive would have had: every range that carries data is
 * listed for the preload (there is no server to stream a miss from, and a
 * miss outside the whitelisted boot frames would trap), all-zero ranges are
 * dropped like any tree's free space.
 */
function localDriveInfo(local: LocalDriveImage): DriveInfo {
    return {
        ahead_read: local.rangeSize,
        range_count: local.rangeCount,
        dropped_ranges: [...local.emptyRanges],
        preload_ranges: "_",
        small_ranges: [],
        cylinders: local.cylinders,
        heads: local.heads,
        sectors: local.sectors,
        sector_size: local.sectorSize,
        size: Math.floor(local.sizeBytes / 1024),
        name: "local",
        url: local.url,
        preloadSizeInBytes: 0,
        sizeInBytes: 0,
        readInBytes: 0,
        writeInBytes: 0,
    };
}

async function fetchDriveInfo(url: string, store: Store): Promise<{ info: DriveInfo, offline: boolean }> {
    try {
        const response = await fetch(url + "/sockdrive.metaj");
        if (!response.ok) {
            throw new Error("metaj fetch failed: " + response.status);
        }
        const bytes = new Uint8Array(await response.arrayBuffer());
        const info = JSON.parse(new TextDecoder().decode(bytes)) as DriveInfo;
        // Refresh the offline copy — a fully prefetched drive must be able
        // to open with no network at all. Cache trouble must never fail an
        // otherwise-successful open.
        await store.put(METAJ_STORE_KEY, bytes, RAW_STORE)
            .catch((e) => console.error("metaj cache write failed", e));
        return { info, offline: false };
    } catch (e) {
        // Offline fallback. A broken cache read must rethrow the ORIGINAL
        // failure, not mask it with its own exception.
        let cached: Uint8Array | null = null;
        try {
            cached = await store.get(METAJ_STORE_KEY, RAW_STORE);
        } catch (e2) {
            console.error("metaj cache read failed", e2);
        }
        if (!cached) throw e;
        return { info: JSON.parse(new TextDecoder().decode(cached)) as DriveInfo, offline: true };
    }
}

/**
 * Opens a streamed drive, or — with `local` — a drive whose image the page
 * holds itself (BackendOptions.localDrives). A local drive reads its ranges
 * from that image and caches none of them; its write overlay works exactly
 * like any other drive's, so saves carry it the same way.
 */
export async function sockdrive(url: string, _onNewRange: (range: number, buffer: Uint8Array) => void,
                                local?: LocalDriveImage): Promise<Drive> {
    const store = await getStore(url);
    // `offline`: the metaj had to come from the offline copy — the network is
    // unreachable, so on-demand synchronous fetches will fail too and full
    // local residency is the only safe mode.
    const { info, offline } = local ?
        { info: localDriveInfo(local), offline: false } :
        await fetchDriveInfo(url, store);
    info.url = url;
    info.readInBytes = 0;
    info.writeInBytes = 0;

    if (info.small_ranges === undefined) {
        info.small_ranges = [];
    }

    let storedSectors = new Map<number, Map<number, Uint8Array>>();
    const serializedSectors = await store.get(0, WRITE_STORE);
    if (serializedSectors) {
        info.writeInBytes = serializedSectors.length;
        storedSectors = deserializeSectors(serializedSectors);
    }

    const storeKeys = new Set<number>();
    // A local drive's image is rebuilt with every load — none of it is cached.
    for (const key of local ? [] : await store.keys(RAW_STORE)) {
        storeKeys.add(key);
    }

    if (info.small_ranges.find((range) => !storeKeys.has(range)) !== undefined) {
        const preload = new Uint8Array(await (await fetch(url + "/preload.raw")).arrayBuffer());

        for (let i = 0; i < info.small_ranges.length; i++) {
            const range = info.small_ranges[i];
            storeKeys.add(range);
            await store.put(range, preload.slice(i * info.ahead_read, (i + 1) * info.ahead_read), RAW_STORE);
        };
    }

    const loaded = new Set<number>();
    const droppedRanges: number[] = info.dropped_ranges;
    const emptyRanges: Set<number> = new Set();
    // The wasm gets the FULL dropped list (its memset branch is the raw
    // truth for those ranges); previously-written sectors inside them are
    // replayed sector-level via wc-sockdrive-restore-sectors, which the
    // wasm consults before the range cache. Only the load-queue seeding
    // stays filtered by storedSectors so written dropped ranges still get
    // a JS-merged delivery when listed for preload.
    for (const next of droppedRanges) {
        emptyRanges.add(next);
        if (!storedSectors.has(next)) {
            loaded.add(next);
        }
    }

    const loadQueue: number[] = [];
    const preloaded = new Set<number>();
    if (info.preload_ranges !== "_") {
        // small_ranges FIRST: they have no per-range `.raw` on the server
        // (their bytes ship inside `preload.raw`, already in the store by
        // now), so the wasm's on-demand synchronous fetch would 404 on them
        // and fall into the asyncify path — which traps outside whitelisted
        // frames. Draining them into the wasm cache before open returns
        // makes that impossible. They're FAT metadata, so front of queue.
        for (const next of info.small_ranges) {
            if (!preloaded.has(next) && !loaded.has(next)) {
                loadQueue.push(next);
                preloaded.add(next);
            }
        }
        for (const next of info.preload_ranges) {
            if (!preloaded.has(next)) {
                loadQueue.push(next);
                preloaded.add(next);
            }
        }
        // Every range already sitting in the local store joins the drain
        // too (cheap IDB reads, no network). This is what makes a fully
        // prefetched "offline install" — and any repeat play — run with
        // full residency: reads never miss, so neither the synchronous
        // fetch (impossible offline) nor the asyncify fallback (traps
        // outside whitelisted frames) is ever needed. Negative keys are
        // reserved slots (cached metaj), not ranges.
        //
        // CD images need a heap guard: the wasm RangeCache NEVER evicts, so
        // draining a fully prefetched multi-hundred-MB CD image online would
        // permanently occupy that much wasm heap. Small CD trees (audio
        // tracks, modest data images) DO drain — an offline-installed game
        // must not re-download its own music mid-play (the synchronous fetch
        // can't consult IDB, so anything not drained here hits the network).
        // Oversized trees keep the old behaviour online: listed preload only,
        // misses stream on demand (browser HTTP cache softens repeats).
        // Offline the full drain is still required regardless of size —
        // there is no network to stream from.
        {
            const residentBytes = [...storeKeys]
                .filter((key) => key >= 0 && key < info.range_count)
                .length * info.ahead_read;
            const drainAll = info.kind !== "cd" || offline ||
                residentBytes <= CD_DRAIN_CAP_BYTES;
            if (drainAll) {
                for (const key of storeKeys) {
                    if (key >= 0 && key < info.range_count && !loaded.has(key) && !preloaded.has(key)) {
                        loadQueue.push(key);
                        preloaded.add(key);
                    }
                }
            }
        }
    } else {
        for (let i = 0; i < info.range_count; i++) {
            if (!loaded.has(i) && !preloaded.has(i)) {
                loadQueue.push(i);
            }
        }
    }

    // validate queue
    {
        const invalidRanges = [];
        for (const range of loadQueue) {
            if (range < 0 || range >= info.range_count) {
                invalidRanges.push(range);
            }
        }
        if (invalidRanges.length > 0) {
            console.error("sockdrive-error: invalid ranges", invalidRanges);
            for (const range of invalidRanges) {
                loadQueue.splice(loadQueue.indexOf(range), 1);
            }
        }
    }

    loadQueue.reverse();


    let rangesToLoad = loadQueue.length;
    for (let i = 0; i < info.range_count; i++) {
        if (!loaded.has(i) && !preloaded.has(i)) {
            rangesToLoad++;
        }
    }
    info.preloadSizeInBytes = loadQueue.length * info.ahead_read;
    info.sizeInBytes = rangesToLoad * info.ahead_read;

    function range(sector: number) {
        return Math.floor(sector * info.sector_size / info.ahead_read);
    };

    function mergeSectorsData(range: number, data: Uint8Array): Uint8Array {
        if (storedSectors.has(range)) {
            for (const [offset, sectorData] of storedSectors.get(range)!.entries()) {
                data.set(sectorData, offset * info.sector_size);
            }
        }

        return data;
    }

    function onNewRange(range: number, buffer: Uint8Array) {
        _onNewRange(range, mergeSectorsData(range, buffer));
    }

    // Ranges a load gave up on — a local image may only be let go when none.
    let unreadRanges = 0;

    async function loadRange(range: number) {
        try {
            if (emptyRanges.has(range)) {
                onNewRange(range, new Uint8Array(info.ahead_read));
                return;
            }

            if (local) {
                onNewRange(range, local.readRange(range));
                return;
            }

            if (storeKeys.has(range)) {
                const buffer = await store.get(range, RAW_STORE);
                if (buffer) {
                    onNewRange(range, buffer);
                    return;
                }
            }

            // Retry transient failures with backoff: a range that never
            // arrives leaves a hole in the wasm RangeCache, and the next
            // read of it has to asyncify-sleep from whatever call chain the
            // game is in — which traps on non-whitelisted frames (dosbox-x
            // build). Better to spend ~30s here than to lose the session.
            let lastError: unknown = null;
            for (let attempt = 0; attempt < 8; attempt++) {
                try {
                    const response = await fetch(url + "/" + range + ".raw");
                    if (!response.ok) {
                        throw new Error("Can't read range " + range +
                            ", network response code is " + response.status);
                    }
                    const buffer = new Uint8Array(await response.arrayBuffer());
                    // The emulator gets the bytes now; the IndexedDB copy is
                    // for the next session and is written in the background.
                    // Waiting for it put a disk write in front of every range
                    // a first load streams. A failed write only means the
                    // range is fetched again next time.
                    store.put(range, buffer, RAW_STORE).catch((e: unknown) =>
                        console.warn("[sockdrive] could not cache range", range, e));
                    onNewRange(range, buffer);
                    return;
                } catch (e) {
                    lastError = e;
                    await new Promise((resolve) =>
                        setTimeout(resolve, Math.min(500 * (attempt + 1), 5000)));
                }
            }
            throw lastError;
        } catch (e) {
            unreadRanges++;
            console.error("Can't read range", range, e);
        } finally {
            info.readInBytes += info.ahead_read;
        }
    }

    // Fully drains the queue before resolving — the recursive
    // fire-and-forget version made completion unobservable, and the wasm
    // side now blocks sockdrive_open on wc-sockdrive-preload-done. A pool of
    // workers, not fixed batches, so one slow range never idles the others.
    // loadRange never rejects (it counts the failure in unreadRanges).
    async function loadFromQueue(): Promise<void> {
        const worker = async () => {
            while (loadQueue.length > 0) {
                const range = loadQueue.pop()!;
                if (!loaded.has(range)) {
                    loaded.add(range);
                    await loadRange(range);
                }
            }
        };
        await Promise.all(Array.from({ length: PRELOAD_CONCURRENCY }, worker));
    };

    function serializeSectors(sectors: Map<number, Map<number, Uint8Array>>): Uint8Array {
        const sectorsData = new Map<number, Uint8Array>();

        for (const [range, rangeSectors] of sectors.entries()) {
            rangeSectors.forEach((data, sector) => {
                sectorsData.set(sector + range * info.ahead_read / info.sector_size, data);
            });
        }

        const chunksSize = info.sector_size + 4;
        const boundSize = compressBound(chunksSize);

        const uncompressedChunk = new Uint8Array(chunksSize);
        const u32uncompressedChunk = new Uint32Array(uncompressedChunk.buffer);

        const compressedChunk = new Uint8Array(boundSize);
        const chunks: Uint8Array[] = [];

        let total = 0;
        sectorsData.forEach((data, sector) => {
            u32uncompressedChunk[0] = sector;
            uncompressedChunk.set(data, 4);
            const compressedSize = compress(uncompressedChunk, compressedChunk, 0, compressedChunk.length);
            if (compressedSize <= 0 || compressedSize >= uncompressedChunk.length) {
                chunks.push(uncompressedChunk.slice(0));
                total += chunksSize;
            } else {
                chunks.push(compressedChunk.slice(0, compressedSize));
                total += compressedSize;
            }
        });

        const payload = new Uint8Array(total + sectorsData.size * 4 + 4);
        payload[0] = sectorsData.size;
        payload[1] = (sectorsData.size & 0x0000ff00) >> 8;
        payload[2] = (sectorsData.size & 0x00ff0000) >> 16;
        payload[3] = (sectorsData.size & 0xff000000) >> 24;


        let offset = 4;
        for (const chunk of chunks) {
            payload[offset] = chunk.length;
            payload[offset + 1] = (chunk.length & 0x0000ff00) >> 8;
            payload[offset + 2] = (chunk.length & 0x00ff0000) >> 16;
            payload[offset + 3] = (chunk.length & 0xff000000) >> 24;
            offset += 4;
            payload.set(chunk, offset);
            offset += chunk.length;
        }

        return payload;
    }

    function deserializeSectors(data: Uint8Array): Map<number, Map<number, Uint8Array>> {
        const sectors = new Map<number, Map<number, Uint8Array>>();
        const count = (data[0] & 0x000000ff) | ((data[1] << 8) & 0x0000ff00) |
            ((data[2] << 16) & 0x00ff0000) | ((data[3] << 24) & 0xff000000);
        const chunkSize = info.sector_size + 4;
        const uncompressedChunk = new Uint8Array(chunkSize);
        const u32uncompressedChunk = new Uint32Array(uncompressedChunk.buffer);

        let offset = 4;
        for (let i = 0; i < count; i++) {
            const compressedSize = (data[offset] & 0x000000ff) | ((data[offset + 1] << 8) & 0x0000ff00) |
                ((data[offset + 2] << 16) & 0x00ff0000) | ((data[offset + 3] << 24) & 0xff000000);
            offset += 4;
            const compressedChunk = data.slice(offset, offset + compressedSize);
            offset += compressedSize;


            let sector;
            let sectorData;
            if (compressedSize === chunkSize) {
                sector = new Uint32Array(compressedChunk.buffer)[0];
                sectorData = compressedChunk.slice(4);
            } else {
                const uncompressedSize = uncompress(compressedChunk, uncompressedChunk, 0, 0);
                if (uncompressedSize !== chunkSize) {
                    console.error("Can't uncompress sectors data, size mismatch",
                        uncompressedSize, "!==", chunkSize, "chunk", i, "offset", offset);
                    return new Map();
                }
                sector = u32uncompressedChunk[0];
                sectorData = uncompressedChunk.slice(4);
            }


            const rangeOfSector = range(sector);

            if (!sectors.has(rangeOfSector)) {
                sectors.set(rangeOfSector, new Map());
            }

            sectors.get(rangeOfSector)!.set(
                sector - (rangeOfSector * info.ahead_read) / info.sector_size,
                sectorData,
            );
        }

        return sectors;
    }

    (window as any).verifySectors = () => {
        if (storedSectors.size === 0) {
            return;
        }

        const serialized = serializeSectors(storedSectors);
        // Create a blob URL for downloading the serialized data
        const blob = new Blob([serialized], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        console.log("Download serialized sectors:", url);
        const deserialized = deserializeSectors(serialized);
        console.log("Comparing sectors and deserialized:");

        // Compare sizes
        console.log("Original sectors size:", storedSectors.size);
        console.log("Deserialized sectors size:", deserialized.size);

        // Compare each range and sector
        storedSectors.forEach((rangeMap, rangeKey) => {
            const deserializedRange = deserialized.get(rangeKey);
            if (!deserializedRange) {
                console.error(`Range ${rangeKey} missing in deserialized data`);
                return;
            }

            rangeMap.forEach((sectorData, sectorKey) => {
                const deserializedSector = deserializedRange.get(sectorKey);
                if (!deserializedSector) {
                    console.error(`Sector ${sectorKey} missing in range ${rangeKey}`);
                    return;
                }

                // Compare sector data
                const match = sectorData.length === deserializedSector.length &&
                    sectorData.every((val, i) => val === deserializedSector[i]);

                if (!match) {
                    console.error(`Data mismatch in range ${rangeKey}, sector ${sectorKey}`);
                    console.log("Original:", sectorData);
                    console.log("Deserialized:", deserializedSector);
                }
            });
        });
    };

    return {
        info,
        range,
        readRangeAsync: async (range: number) => {
            if (!loaded.has(range)) {
                loaded.add(range);
                loadRange(range);
            }
        },
        ready: async () => {
            await loadFromQueue();
            // Every range of a local image is in the emulator now, and it
            // never asks for one it holds — the page's copy can go.
            if (local && unreadRanges === 0) {
                local.release?.();
            }
        },
        write: (sector: number, buffer: Uint8Array) => {
            // Never let an oversized buffer into the overlay: serializeSectors
            // packs each sector into a sector_size+4 scratch and an oversized
            // entry throws RangeError at save time (and range patching in
            // decorateWithWrites would overflow too). The worker glue slices
            // writes per-drive, but if a mis-sized buffer ever arrives the
            // first sector_size bytes are the true sector — keep exactly those.
            if (buffer.length > info.sector_size) {
                buffer = buffer.subarray(0, info.sector_size);
            }
            const rangeOfSector = range(sector);
            if (!storedSectors.has(rangeOfSector)) {
                storedSectors.set(rangeOfSector, new Map());
            }
            storedSectors.get(rangeOfSector)!.set(
                sector - (rangeOfSector * info.ahead_read) / info.sector_size,
                buffer);
        },
        writtenSectors: () => {
            const out: Array<{ sector: number; data: Uint8Array }> = [];
            for (const [rangeOfSector, rangeSectors] of storedSectors.entries()) {
                for (const [offset, data] of rangeSectors.entries()) {
                    out.push({
                        sector: offset + (rangeOfSector * info.ahead_read) / info.sector_size,
                        data,
                    });
                }
            }
            return out;
        },
        storeRange: async (rangeIndex: number, data: Uint8Array) => {
            if (local) {
                return;
            }
            if (!loaded.has(rangeIndex)) {
                loaded.add(rangeIndex);
                info.readInBytes += info.ahead_read;
            }
            await store.put(rangeIndex, data, RAW_STORE);
            storeKeys.add(rangeIndex);
        },
        persist: async () => {
            const serialized = serializeSectors(storedSectors);
            if (serialized.byteLength > 4) {
                return serialized;
            }
            return null;
        },
    };
}
